"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, Scale, ShieldCheck, FileSpreadsheet } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const pillars = [{
  id: "independence",
  title: "Multi-carrier independence",
  description: "We can compare available insurance and annuity products from multiple carriers to help you understand your options.",
  icon: Scale
}, {
  id: "fiduciary",
  title: "Personalized educational approach",
  description: "Conversations are centered on your insurance needs, questions, priorities, and circumstances.",
  icon: ShieldCheck
}, {
  id: "transparency",
  title: "Plain-language transparency",
  description: "We explain how life insurance, fixed and fixed indexed annuities, and MYGAs work, including important features, limitations, charges, and surrender periods.",
  icon: FileSpreadsheet
}];
const highlights = ["A calm, unhurried first conversation.", "Direct review of existing policies", "Clear written breakdown of all options"];
export default function Consulting() {
  const shouldReduceMotion = useReducedMotion();
  const motionProps = shouldReduceMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    animate: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 14
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.2
    },
    transition: {
      duration: 0.35,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  return <section id="why-work-with-djtd-consulting" data-nav="light" className="bg-background text-foreground py-20 md:py-28">
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left Column: Narrative & Values */}
          <motion.div {...motionProps} className="lg:col-span-7">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="rounded-full border-border bg-muted/60 px-3.5 py-1 text-xs font-semibold tracking-wider text-foreground uppercase">
                Why work with us
              </Badge>
            </div>

            <h2 className="font-default text-foreground mt-4 text-3xl font-bold tracking-tight sm:text-4xl lg:text-[2.65rem] lg:leading-[1.2]">
              Independent guidance, clearly explained.
            </h2>

            {/* Architectural Anchor Dot Rule */}
            <div className="my-6 flex items-center gap-2">
              <div className="size-1.5 rounded-full bg-primary" />
              <div className="h-px flex-1 bg-border" />
            </div>

            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              Planning for retirement and family protection should feel like an open conversation, not a transaction. We take an educational approach so you understand each option and move forward with total confidence.
            </p>

            {/* Pillars List */}
            <div className="mt-8 space-y-6">
              {pillars.map((pillar, i) => {
              const IconComponent = pillar.icon;
              return <div key={pillar.id} data-index={i} className="flex items-start gap-4 rounded-xl border border-border bg-card p-5 text-card-foreground shadow-xs">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted text-primary">
                      <IconComponent className="size-5" />
                    </div>
                    <div>
                      <h3 className="font-default text-foreground text-base font-semibold">
                        {pillar.title}
                      </h3>
                      <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
                        {pillar.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            {/* Trust Checklist */}
            <div className="mt-8 border-t border-border pt-6">
              <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                {highlights.map((highlight, index) => <li key={highlight} data-index={index} className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                    <CheckCircle2 className="size-4 shrink-0 text-primary" />
                    <span>{highlight}</span>
                  </li>)}
              </ul>
            </div>

            {/* Action Link */}
            <div className="mt-8 flex items-center">
              <Button asChild variant="outline" size="lg" className="rounded-lg font-medium">
                <Link to="/FAQKnowledgeCenterPage">
                  <span>Browse knowledge center</span>
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Visual Frame */}
          <motion.div {...motionProps} transition={shouldReduceMotion ? {} : {
          duration: 0.35,
          delay: 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5">
            <div className="relative rounded-2xl border border-border bg-card p-3 shadow-md">
              <div className="overflow-hidden rounded-xl bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796870/img/consultant_guidance_meeting.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796870/img/consultant_guidance_meeting.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796870/img/consultant_guidance_meeting.png-wvc-sizes" alt="Financial consultant in a calm, collaborative review session" loading="lazy" decoding="async" className="aspect-[4/5] w-full object-cover object-center" />
              </div>

              {/* Quiet Callout Plinth */}
              <div className="mt-4 rounded-xl border border-border bg-[hsl(150_20%_95%)] p-4 sm:p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
                      Our approach,
                    </p>
                    <p className="font-default text-foreground text-sm font-semibold sm:text-base">
                      We listen. We compare. We explain. You decide.
                    </p>
                  </div>
                  <Badge variant="secondary" className="rounded-md font-medium text-xs">
                    Independent
                  </Badge>
                </div>
                <p className="text-muted-foreground mt-2 text-xs leading-relaxed">
                  We review your existing coverage and retirement accounts without cost or commitment.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}